import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { API } from "@/lib/constants";
import { useAdminAuth, getAdminHeaders, getAdminSessionToken, formatDate } from "@/lib/admin";
import { Button } from "@/components/ui/button";

export default function AdminCareersPage({ language }) {
  const navigate = useNavigate();
  const authenticated = useAdminAuth(navigate);
  const adminSessionToken = getAdminSessionToken();
  const [submissions, setSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (!authenticated) return;
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/careers`, { headers: getAdminHeaders() });
        setSubmissions(res.data || []);
      } catch (err) {
        console.error("Failed to load career submissions:", err);
      }
      setIsLoading(false);
    };
    fetchData();
  }, [authenticated]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this submission?")) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API}/careers/${id}`, { headers: getAdminHeaders() });
      setSubmissions((prev) => prev.filter((s) => s.id !== id));
    } catch {
      alert("Failed to delete submission.");
    }
    setDeletingId(null);
  };

  if (!authenticated) return null;

  return (
    <div className="min-h-screen bg-[#f4f6fb]">
      <Header language={language} />
      <main className="pt-28 pb-20">
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <Link
                to="/admin-dashboard"
                className="text-xs uppercase tracking-widest text-[#c2873c] hover:text-[#003366] transition-colors"
                data-testid="admin-careers-back"
              >
                &larr; Back to Dashboard
              </Link>
              <h1
                className="font-heading text-3xl sm:text-4xl text-[#003366] mt-3"
                data-testid="admin-careers-title"
              >
                Career Submissions
              </h1>
              <p className="mt-2 text-sm text-slate-600">
                {submissions.length} total submission{submissions.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>

          <div className="mt-8 space-y-4" data-testid="admin-careers-list">
            {isLoading ? (
              <p className="text-slate-500 p-6 bg-white border border-slate-200">
                Loading submissions...
              </p>
            ) : submissions.length === 0 ? (
              <p
                className="text-slate-500 p-6 bg-white border border-slate-200"
                data-testid="admin-careers-empty"
              >
                No career submissions yet.
              </p>
            ) : (
              submissions.map((s, index) => (
                <div
                  key={s.id}
                  className="bg-white border border-slate-200 shadow-sm"
                  data-testid={`admin-career-card-${s.id}`}
                >
                  <div
                    className="p-6 cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => setExpandedId(expandedId === s.id ? null : s.id)}
                    data-testid={`admin-career-toggle-${s.id}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-start gap-4">
                        <span
                          className="flex-shrink-0 w-8 h-8 bg-[#003366] text-white text-sm font-bold flex items-center justify-center"
                          data-testid={`admin-career-sno-${index}`}
                        >
                          {index + 1}
                        </span>
                        <div>
                          <p
                            className="font-heading text-xl text-[#003366]"
                            data-testid={`admin-career-name-${s.id}`}
                          >
                            {s.full_name || s.name || "-"}
                          </p>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-slate-500">
                            <span>{s.email}</span>
                            <span>{s.phone}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs uppercase tracking-wider px-3 py-1 border border-slate-200 text-slate-600">
                          {s.source === "share" ? "Resume Share" : "Application"}
                        </span>
                        <span className="text-xs text-slate-400">{formatDate(s.created_at)}</span>
                        <span className="text-[#003366] text-sm">
                          {expandedId === s.id ? "\u25B2" : "\u25BC"}
                        </span>
                      </div>
                    </div>
                  </div>
                  {expandedId === s.id && (
                    <div
                      className="border-t border-slate-200 p-6 bg-slate-50"
                      data-testid={`admin-career-details-${s.id}`}
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs uppercase tracking-wider text-slate-400">Role</p>
                          <p className="mt-1 text-slate-700 font-medium">{s.role || "-"}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wider text-slate-400">
                            Experience
                          </p>
                          <p className="mt-1 text-slate-700 font-medium">
                            {s.experience_years ?? s.experience ?? "-"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wider text-slate-400">
                            Location
                          </p>
                          <p className="mt-1 text-slate-700 font-medium">{s.location || "-"}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wider text-slate-400">Source</p>
                          <p className="mt-1 text-slate-700 font-medium">
                            {s.source === "share" ? "Resume Share" : "Apply Profile"}
                          </p>
                        </div>
                      </div>
                      {s.message && (
                        <div className="mt-4">
                          <p className="text-xs uppercase tracking-wider text-slate-400">Message</p>
                          <p
                            className="mt-1 text-slate-700 bg-white border border-slate-200 p-4"
                            data-testid={`admin-career-message-${s.id}`}
                          >
                            {s.message}
                          </p>
                        </div>
                      )}
                      <div className="mt-4 flex flex-wrap gap-3">
                        {s.resume_path && (
                          <a
                            href={`${API}/careers/${s.id}/resume?token=${encodeURIComponent(adminSessionToken || "")}`}
                            className="inline-flex items-center gap-2 bg-[#003366] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#002244] transition-colors"
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`admin-career-resume-${s.id}`}
                          >
                            Download Resume PDF
                          </a>
                        )}
                        <Button
                          variant="outline"
                          className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white rounded-none px-4 py-2 text-xs font-bold uppercase tracking-wider"
                          onClick={() => handleDelete(s.id)}
                          disabled={deletingId === s.id}
                          data-testid={`admin-career-delete-${s.id}`}
                        >
                          {deletingId === s.id ? "Deleting..." : "Delete Submission"}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </section>
      </main>
      <Footer language={language} />
    </div>
  );
}
